import React from 'react';
import { motion } from 'framer-motion';
import './privacypolicy.css';

function PrivacyPolicy() {
  const policySections = [
    {
      title: "Information We Collect",
      content: [
        "We collect personal information you provide directly to us, such as your name, email address, phone number, and any other information you choose to provide when you register for an account, subscribe to our services, or contact us.",
        "We automatically collect certain information about your device and how you interact with our website, including IP address, browser type, operating system, referring URLs, device information, pages visited, and dates/times of visits.",
        "We use cookies and similar tracking technologies to track activity on our website and hold certain information to improve your experience."
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "To provide, maintain, and improve our services",
        "To communicate with you about products, services, offers, and events",
        "To monitor and analyze trends, usage, and activities in connection with our services",
        "To detect, investigate, and prevent fraudulent transactions and other illegal activities",
        "To comply with legal obligations and protect our rights and the rights of others"
      ]
    },
    {
      title: "Information Sharing",
      content: [
        "We may share your information with third-party vendors and service providers who perform services on our behalf.",
        "We may disclose your information if required by law or in response to valid requests by public authorities.",
        "In connection with any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.",
        "We may share aggregated or de-identified information that cannot reasonably be used to identify you."
      ]
    },
    {
      title: "Data Security",
      content: [
        "We implement appropriate technical and organizational measures to protect the security of your personal information.",
        "We use encryption to protect sensitive information transmitted online and also protect your information offline.",
        "While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or method of electronic storage is 100% secure."
      ]
    },
    {
      title: "Your Rights",
      content: [
        "Access, correct, update, or request deletion of your personal information.",
        "Object to processing of your personal information, ask us to restrict processing, or request portability.",
        "Opt out of marketing communications we send you at any time.",
        "Withdraw your consent at any time where we rely on your consent to process your personal information.",
        "Complain to a data protection authority about our collection and use of your personal information."
      ]
    },
    {
      title: "Changes to This Policy",
      content: [
        "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.",
        "We will let you know via email and/or a prominent notice on our service, prior to the change becoming effective.",
        "You are advised to review this Privacy Policy periodically for any changes."
      ]
    }
  ];

  return (
    <motion.div 
      className="privacy-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="privacy-header">
        <motion.h1
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Privacy Policy
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="last-updated"
        >
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </motion.p>
      </div>

      <motion.div 
        className="privacy-intro"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <p>
          Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, 
          and safeguard your information when you visit our website or use our services. Please read this 
          policy carefully. If you do not agree with the terms of this privacy policy, please do not 
          access the site or use our services.
        </p>
      </motion.div>

      <div className="policy-sections">
        {policySections.map((section, index) => (
          <motion.div 
            key={index}
            className="policy-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
          >
            <h2 className="section-title">
              <span className="section-number">{index + 1}.</span> {section.title}
            </h2>
            <ul className="section-content">
              {section.content.map((item, itemIndex) => (
                <motion.li
                  key={itemIndex}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 + (index * 0.1) + (itemIndex * 0.05) }}
                >
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.div 
        className="contact-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1 }}
      >
        <h2>Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at:
        </p>
        <div className="contact-methods">
          <a href="mailto:privacy@example.com" className="contact-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#3F51B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 6L12 13L2 6" stroke="#3F51B5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            info@optometryassociation.lk
          </a>
          <a href="tel:+1234567890" className="contact-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22 20.47 21.55 20.92 21 20.92C12.61 20.92 3.08 11.39 3.08 3C3.08 2.45 3.53 2 4.08 2H7.08C7.63 2 8.08 2.45 8.08 3C8.08 5.85 8.71 8.64 9.91 11.19C10.09 11.57 10.03 12.02 9.75 12.34L7.13 15.35C8.91 18.06 11.59 20.41 14.66 21.25L17.67 18.63C17.99 18.35 18.44 18.3 18.82 18.47C21.36 19.68 24.15 20.31 27 20.31C27.55 20.31 28 20.76 28 21.31V24.31C28 24.86 27.55 25.31 27 25.31C18.61 25.31 7.69 14.39 7.69 6C7.69 5.45 8.14 5 8.69 5H11.69C12.24 5 12.69 5.45 12.69 6C12.69 8.24 13.37 10.45 14.67 12.38C14.94 12.79 14.91 13.32 14.59 13.7L12.31 16.38C14.05 18.58 16.42 20.25 19.13 20.96C19.5 21.05 19.85 20.89 20.04 20.57L21.72 17.82C21.89 17.53 21.89 17.18 21.72 16.89C21.56 16.63 21.28 16.48 21 16.48L17 16.48C16.45 16.48 16 16.93 16 17.48C16 18.92 16.32 20.34 16.93 21.65C16.63 21.6 16.33 21.54 16.04 21.47L13.35 20.81C11.8 20.41 10.37 19.65 9.15 18.59C7.93 17.53 6.96 16.21 6.32 14.72L5.54 12.96C5.46 12.66 5.4 12.36 5.35 12.06C6.66 12.67 8.08 13 9.52 13C10.07 13 10.52 12.55 10.52 12V8.03C10.52 7.75 10.37 7.48 10.11 7.33C9.84 7.17 9.52 7.16 9.24 7.29L6.5 8.97C6.18 9.13 5.99 9.46 6 9.82C6.06 12.28 7.18 14.6 8.99 16.4C10.8 18.21 13.12 19.33 15.58 19.39C15.94 19.4 16.27 19.21 16.43 18.89L18.11 16.14C18.24 15.86 18.23 15.54 18.07 15.27C17.92 15.01 17.65 14.86 17.37 14.86H13.37C12.82 14.86 12.37 15.31 12.37 15.86Z" fill="#3F51B5"/>
            </svg>
            0117 700 903
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default PrivacyPolicy;